import './i18n/config';

import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { trackPageView } from './utils/analytics';

// ✅ CSS e librerie
import './styles/App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/ToastCustom.css';

// ✅ Componenti critici (caricano subito)
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';

// ✅ LAZY LOAD: Tutte le altre pagine
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PDPDetail = lazy(() => import('./components/PDPDetail'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const CancelPage = lazy(() => import('./pages/CancelPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const Contact = lazy(() => import('./pages/Contact'));
const EmailConfirmPage = lazy(() => import('./pages/EmailConfirmPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));

// 🔥 PricingPage import removed — /pricing now redirects to /about (see Routes below).
// The PricingPage.jsx file itself is kept in the repo for records.


// 🔥 Add animation for cookie banner
const cookieBannerStyles = `
  @keyframes slideUpFade {
    0% {
      opacity: 0;
      transform: translateY(50px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = cookieBannerStyles;
  document.head.appendChild(styleSheet);
}

function App() {
  const location = useLocation();
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  // ✅ OTTIMIZZATO: Loading component per Suspense
  const PageLoader = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(37, 99, 235, 0.15)',
        borderTop: '3px solid rgb(168, 85, 247)',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }}></div>
    </div>
  );

  // 🔥 FIX: Check cookie consent with expiration (365 days)
  useEffect(() => {
    const checkConsent = () => {
      const consentData = localStorage.getItem('betterpdp_consent');

      if (!consentData) {
        setShowCookieBanner(true);
        return;
      }

      try {
        const { status, timestamp } = JSON.parse(consentData);
        const now = Date.now();
        const oneYear = 365 * 24 * 60 * 60 * 1000;

        if (status !== 'accepted') {
          localStorage.removeItem('betterpdp_consent');
          setShowCookieBanner(true);
          return;
        }

        if (now - timestamp > oneYear) {
          console.log('🍪 Cookie consent expired, showing banner again');
          localStorage.removeItem('betterpdp_consent');
          setShowCookieBanner(true);
        } else {
          setShowCookieBanner(false);

          if (window.gtag) {
            window.gtag('consent', 'update', {
              'analytics_storage': 'granted'
            });
          }
        }
      } catch (e) {
        console.log('🍪 Old consent format detected, showing banner');
        localStorage.removeItem('betterpdp_consent');
        setShowCookieBanner(true);
      }
    };

    checkConsent();
  }, []);

  // Track pageview on route change
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  const handleAcceptCookies = () => {
    const consentData = {
      status: 'accepted',
      timestamp: Date.now()
    };

    localStorage.setItem('betterpdp_consent', JSON.stringify(consentData));

    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });

      window.gtag('event', 'cookie_consent_granted', {
        'event_category': 'consent',
        'event_label': 'user_accepted'
      });

      console.log('✅ Cookie consent granted (no reload needed)');
    }

    setShowCookieBanner(false);
  };

  const handleDeclineCookies = () => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });

      console.log('🚫 Cookie consent declined (will ask again next session)');
    }

    setShowCookieBanner(false);
  };

  return (
    <HelmetProvider>
      <AuthProvider>

        {/* ✅ CUSTOM COOKIE BANNER */}
        {showCookieBanner && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(15, 23, 42, 0.98)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(37, 99, 235, 0.15)',
            padding: '24px',
            zIndex: 9999,
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.3)',
            animation: 'slideUpFade 0.4s ease-out'
          }}>
            <div style={{
              maxWidth: '1200px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: '32px', lineHeight: 1 }}>🍪</span>
              <div style={{ flex: 1, minWidth: '300px' }}>
                <strong style={{
                  color: '#fff',
                  fontSize: '18px',
                  display: 'block',
                  marginBottom: '8px',
                  lineHeight: 1.3
                }}>
                  We value your privacy
                </strong>
                <p style={{
                  margin: '0',
                  color: '#cbd5e1',
                  fontSize: '14px',
                  lineHeight: 1.6
                }}>
                  We use cookies to analyze traffic, personalize content, and improve your experience.
                  By clicking "Accept All Cookies", you consent to our use of cookies.
                  {' '}
                  <a
                    href="/privacy"
                    style={{
                      color: '#2563eb',
                      fontWeight: 600,
                      textDecoration: 'underline'
                    }}
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={handleAcceptCookies}
                  style={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: '700',
                    borderRadius: '8px',
                    padding: '12px 32px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.35)';
                  }}
                >
                  Accept All Cookies
                </button>
                <button
                  onClick={handleDeclineCookies}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#64748b',
                    fontSize: '13px',
                    fontWeight: '400',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#64748b';
                  }}
                >
                  Essential Only
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ✅ END COOKIE BANNER */}

        <div className="App">
          <ScrollToTop />

          <Navbar />

          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/analysis/:id" element={<PDPDetail />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/cancel" element={<CancelPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/auth/confirm" element={<EmailConfirmPage />} />

              {/* 🔥 /pricing now redirects to /about — donation-only framing */}
              <Route path="/pricing" element={<Navigate to="/about" replace />} />

              {/* Protected */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />

              {/* Catch-all: unknown URLs go home instead of showing a blank page */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>

          <Footer />

          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick={false}
            closeButton={false}
            rtl={false}
            pauseOnFocusLoss={false}
            draggable={false}
            pauseOnHover={false}
            theme="dark"
            limit={3}
          />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;